import { useGsap } from "@/hooks/useGsap";
import { gsap, ScrollTrigger, EASE, prefersReducedMotion } from "@/lib/motion";
import { WRAP } from "@/components/story/layout";

/**
 * Moment 5: the pause.
 *
 * Almost nothing on screen. One hairline drawing itself down the page, one
 * statement, one paragraph. This section exists to slow the reader down before
 * the closing ask, so it deliberately carries no link and no button.
 */
export default function PhilosophyScene() {
  const scope = useGsap((ctx, el) => {
    if (prefersReducedMotion()) return;

    const line = el.querySelector("[data-line]");
    const items = el.querySelectorAll("[data-reveal-item]");

    try {
      ScrollTrigger.create({
        trigger: el,
        start: "top 82%",
        once: true,
        onEnter: () => {
          const tl = gsap.timeline({ defaults: { ease: EASE } });
          if (line) tl.fromTo(line, { scaleY: 0 }, { scaleY: 1, duration: 1.4 });
          tl.fromTo(
            items,
            { opacity: 0, y: 20 },
            { opacity: 1, y: 0, duration: 0.8, stagger: 0.14 },
            "-=1.05"
          );
        },
      });
    } catch {
      // Content is readable without this.
    }
  }, []);

  return (
    <section ref={scope} className="border-b border-ink" data-testid="section-philosophy">
      <div className={`${WRAP} story-pad`}>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="hidden lg:flex lg:col-span-1 justify-center">
            <div data-line className="philosophy-line h-full min-h-[220px]" aria-hidden="true" />
          </div>

          <div className="lg:col-span-8 lg:col-start-3">
            <p data-reveal-item className="story-index">05 / What we believe</p>

            <h2 data-reveal-item className="display story-h2 mt-6 max-w-[24ch]">
              Good software should look obvious
              once it is built.
            </h2>

            <p data-reveal-item className="mt-8 max-w-[42ch] story-lead text-ink/75">
              The hard part is rarely the code. The hard part is deciding what deserves
              to exist, how it should work, and whether the people doing the job will
              actually use it on a Tuesday afternoon.
            </p>

            <p data-reveal-item className="mt-6 max-w-[44ch] story-body text-ink/60">
              That is why the first conversation is fifteen minutes and free. Sometimes
              the honest answer is that nothing needs building yet.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
